import i18n from '../i18n/config';
// arsenal-tpms/frontend/src/hooks/useLanguage.js
import { useState, useEffect, useCallback, useReducer, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LOCAL_STORAGE_KEYS } from '../utils/constants';

const SUPPORTED_LANGUAGES = [
    { code: 'es', labelKey: 'createUser.languages.es', nativeLabel: 'Español', flag: '🇪🇸' },
    { code: 'en', labelKey: 'createUser.languages.en', nativeLabel: 'English', flag: '🇺🇸' },
    { code: 'fr', labelKey: 'createUser.languages.fr', nativeLabel: 'Français', flag: '🇫🇷' },
    { code: 'de', labelKey: 'createUser.languages.de', nativeLabel: 'Deutsch', flag: '🇩🇪' },
    { code: 'it', labelKey: 'createUser.languages.it', nativeLabel: 'Italiano', flag: '🇮🇹' },
    { code: 'pt', labelKey: 'createUser.languages.pt', nativeLabel: 'Português', flag: '🇵🇹' },
    { code: 'ru', labelKey: 'createUser.languages.ru', nativeLabel: 'Русский', flag: '🇷🇺' },
    { code: 'zh-CN', labelKey: 'createUser.languages.zh', nativeLabel: '中文', flag: '🇨🇳' },
    { code: 'ja', labelKey: 'createUser.languages.ja', nativeLabel: '日本語', flag: '🇯🇵' }
];

/**
 * Custom hook for managing language preferences
 * Handles synchronization between i18next, localStorage, and user profile
 */
export const useLanguage = () => {
    const { i18n, t } = useTranslation();
    const [isChangingLanguage, setIsChangingLanguage] = useState(false);
    const [, forceUpdate] = useReducer(x => x + 1, 0);
    const langRef = useRef(i18n.language);

    // Force re-render on language change to ensure UI updates even if
    // react-i18next's useSyncExternalStore subscription doesn't trigger
    useEffect(() => {
        i18n.on('languageChanged', forceUpdate);
        return () => i18n.off('languageChanged', forceUpdate);
    }, [i18n]);

    // Keep ref in sync for stable callbacks
    useEffect(() => {
        langRef.current = i18n.language;
    }, [i18n.language]);

    // Get current language
    const currentLanguage = i18n.language;

    /**
     * Change language and update localStorage
     * @param {string} languageCode - Language code
     * @param {boolean} persistToProfile - Whether to persist to user profile
     * @returns {Promise<boolean>} Success status
     */
    const changeLanguage = useCallback(async (languageCode, persistToProfile = true) => {
        if (isChangingLanguage) return false;

        setIsChangingLanguage(true);

        try {
            // Change i18next language
            await i18n.changeLanguage(languageCode);

            // Update localStorage
            localStorage.setItem(LOCAL_STORAGE_KEYS.LANGUAGE, languageCode);

            console.log(`Language changed to: ${languageCode}`);

            return true;
        } catch (error) {
            console.error(t('hardcoded.hooks_uselanguage.error_changing_language'), error);
            return false;
        } finally {
            setIsChangingLanguage(false);
        }
    }, [i18n, isChangingLanguage, t]);

    /**
     * Change language and optionally persist to user profile via callback.
     * Does NOT reload the page - i18next's languageChanged event triggers
     * React re-renders via react-i18next's bindI18n: 'languageChanged' config.
     */
    const applyLanguageAndReload = useCallback(async (languageCode, persistFn) => {
        if (!languageCode || languageCode === i18n.language) return false;

        setIsChangingLanguage(true);
        try {
            await i18n.changeLanguage(languageCode);
            localStorage.setItem(LOCAL_STORAGE_KEYS.LANGUAGE, languageCode);

            if (typeof persistFn === 'function') {
                try {
                    await persistFn(languageCode);
                } catch (error) {
                    console.error('Error persisting language preference:', error);
                }
            }
            return true;
        } catch (error) {
            console.error(t('hardcoded.hooks_uselanguage.error_changing_language'), error);
            return false;
        } finally {
            setIsChangingLanguage(false);
        }
    }, [i18n, t]);

    /**
     * Initialize language from user profile
     * @param {Object} userProfile - User profile data
     */
    const initializeLanguageFromProfile = useCallback(async (userProfile) => {
        if (!userProfile?.language) return;

        const profileLanguage = userProfile.language;
        const currentLang = langRef.current;

        // Only change if different from current language
        if (profileLanguage !== currentLang) {
            await changeLanguage(profileLanguage, false); // Don't persist back to profile
        }
    }, [changeLanguage]);

    /**
     * Reset language to browser default
     */
    const resetToSystemLanguage = useCallback(async () => {
        // Remove from localStorage to let i18next detect from browser
        localStorage.removeItem(LOCAL_STORAGE_KEYS.LANGUAGE);

        // Detect language again
        const detectedLanguage = i18n.services.languageDetector.detect();
        const supportedLanguage = Array.isArray(detectedLanguage)
            ? detectedLanguage[0]
            : detectedLanguage;

        await changeLanguage(supportedLanguage || 'es', false);
    }, [i18n, changeLanguage]);

    /**
     * Get available languages with labels
     */
    const getAvailableLanguages = useCallback(() => {
        return SUPPORTED_LANGUAGES.map((language) => ({
            code: language.code,
            label: language.nativeLabel,
            nativeLabel: language.nativeLabel,
            flag: language.flag
        }));
    }, []);

    const getLanguageMeta = useCallback((languageCode) => {
        const language = getAvailableLanguages().find((lang) => lang.code === languageCode);
        if (!language) {
            return {
                code: languageCode,
                label: languageCode,
                nativeLabel: languageCode,
                flag: '🌐'
            };
        }
        return language;
    }, [getAvailableLanguages]);

    /**
     * Get language label by code
     */
    const getLanguageLabel = useCallback((languageCode) => {
        const languages = getAvailableLanguages();
        const language = languages.find(lang => lang.code === languageCode);
        return language ? language.label : languageCode;
    }, [getAvailableLanguages]);

    /**
     * Check if a language is supported
     */
    const isLanguageSupported = useCallback((languageCode) => {
        return SUPPORTED_LANGUAGES.some((lang) => lang.code === languageCode);
    }, []);

    // Effect to sync with localStorage changes (for multiple tabs)
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === LOCAL_STORAGE_KEYS.LANGUAGE && e.newValue) {
                const newLanguage = e.newValue;
                if (newLanguage !== i18n.language && isLanguageSupported(newLanguage)) {
                    i18n.changeLanguage(newLanguage);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [i18n, isLanguageSupported]);

    return {
        currentLanguage,
        isChangingLanguage,
        changeLanguage,
        applyLanguageAndReload,
        initializeLanguageFromProfile,
        resetToSystemLanguage,
        getAvailableLanguages,
        getLanguageMeta,
        getLanguageLabel,
        isLanguageSupported,
        t // Re-export for convenience
    };
};

export default useLanguage;